import React, { memo } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  Tooltip,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { SEARCH_COLUMNS } from '../constants';

interface SearchBarProps {
  searchTerm: string;
  searchColumn: string;
  onSearchTermChange: (value: string) => void;
  onSearchColumnChange: (value: string) => void;
  onMassReportsLock: () => void;
  onMassPOSLock: () => void;
  /** Флаги оператора из ответа сервера (users.create, users.toggle_status) */
  canCreate?: boolean;
  canToggleStatus?: boolean;
  onOpenColumnConfig?: () => void;
  onOpenCreateUser?: () => void;
}

const SearchBar: React.FC<SearchBarProps> = memo(({
  searchTerm,
  searchColumn,
  onSearchTermChange,
  onSearchColumnChange,
  onMassReportsLock,
  onMassPOSLock,
  canCreate = false,
  canToggleStatus = false,
  onOpenColumnConfig,
  onOpenCreateUser
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        bgcolor: '#ffffff',
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
      }}
    >
      {/* Поле поиска */}
      <TextField
        size="small"
        placeholder="Поиск пользователей..."
        value={searchTerm}
        onChange={(e) => onSearchTermChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
            </InputAdornment>
          ),
          sx: {
            borderRadius: '8px',
            bgcolor: '#f8fafc',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: '#2563eb' }
          }
        }}
        sx={{
          flexGrow: 1,
          minWidth: 240
        }}
      />

      {/* Выбор колонки поиска */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="search-column-label" sx={{ fontSize: '0.875rem' }}>
          Поиск по
        </InputLabel>
        <Select
          labelId="search-column-label"
          value={searchColumn}
          onChange={(e) => onSearchColumnChange(e.target.value)}
          label="Поиск по"
          sx={{
            borderRadius: '8px',
            bgcolor: '#f8fafc',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' }
          }}
        >
          {SEARCH_COLUMNS.map(column => (
            <MenuItem key={column.value} value={column.value} sx={{ fontSize: '0.875rem' }}>
              {column.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Кнопки действий и настройки таблицы */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 0, sm: 'auto' } }}>
        {/* Массовая блокировка касается только сотрудников в зоне оператора - это ограничивает сервер */}
        {canToggleStatus && (
          <>
            <Tooltip title="Двойной клик для блокировки отчетов у всех">
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onDoubleClick={onMassReportsLock}
                  startIcon={<LockOutlinedIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    height: 40,
                    px: 2,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderColor: '#fecaca',
                    bgcolor: '#fef2f2',
                    color: '#b91c1c',
                    '&:hover': {
                      bgcolor: '#fee2e2',
                      borderColor: '#fca5a5'
                    }
                  }}
                >
                  Блок. отчетов
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Двойной клик для блокировки касс у всех">
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onDoubleClick={onMassPOSLock}
                  startIcon={<PointOfSaleIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    height: 40,
                    px: 2,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderColor: '#fecaca',
                    bgcolor: '#fef2f2',
                    color: '#b91c1c',
                    '&:hover': {
                      bgcolor: '#fee2e2',
                      borderColor: '#fca5a5'
                    }
                  }}
                >
                  Блок. касс
                </Button>
              </span>
            </Tooltip>
          </>
        )}

        {/* Кнопка создания пользователя */}
        {onOpenCreateUser && canCreate && (
          <Button
            variant="contained"
            size="small"
            onClick={onOpenCreateUser}
            startIcon={<PersonAddIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: 40,
              px: 2,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              bgcolor: '#1570ef',
              color: '#ffffff',
              boxShadow: '0 2px 8px rgba(21, 112, 239, 0.25)',
              '&:hover': {
                bgcolor: '#105ec2'
              }
            }}
          >
            Создать пользователя
          </Button>
        )}

        {/* Кнопка настройки колонок */}
        {onOpenColumnConfig && (
          <Tooltip title="Настроить отображение и порядок колонок">
            <Button
              variant="outlined"
              size="small"
              onClick={onOpenColumnConfig}
              startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: 18, color: '#475569' }} />}
              sx={{
                height: 40,
                px: 1.8,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc',
                color: '#334155',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  borderColor: '#94a3b8',
                  color: '#0f172a'
                }
              }}
            >
              Колонки
            </Button>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
